import React, { useState } from 'react';

export const ConditionArticles = ({
  condition = 'Lupus',
  conditionColor = '#9b59b6',
  onBack,
  onArticleClick,
  onCommunityClick,
  articles = [
    {
      id: 1,
      title: 'Can You Die From Lupus? What 650 Real Patients Want You to Know',
      excerpt: 'The question haunts nearly every lupus patient at some point: Can this disease kill me? We asked 650 real lupus patients about their fears, experiences, and what they wish they\'d known.',
      readTime: '8 min',
      date: 'Jan 20, 2025',
      category: 'Living with Lupus',
      featured: true
    },
    {
      id: 2,
      title: 'Understanding Lupus Flares: Triggers, Symptoms, and Management',
      excerpt: 'Learn what causes lupus flares, how to recognize early warning signs, and strategies real patients use to manage them.',
      readTime: '6 min',
      date: 'Jan 18, 2025',
      category: 'Symptom Management'
    },
    {
      id: 3,
      title: 'The Lupus Diet: What Patients Say Actually Works',
      excerpt: 'We surveyed hundreds of lupus patients about dietary changes. Here\'s what they found helpful.',
      readTime: '7 min',
      date: 'Jan 15, 2025',
      category: 'Nutrition'
    },
    {
      id: 4,
      title: 'Lupus and Mental Health: Breaking the Silence',
      excerpt: 'Depression, anxiety, and brain fog affect many lupus patients. Real stories of coping and finding support.',
      readTime: '9 min',
      date: 'Jan 12, 2025',
      category: 'Mental Health'
    },
    {
      id: 5,
      title: 'Navigating Lupus Treatment Options in 2025',
      excerpt: 'From Plaquenil to biologics like Saphnelo and Benlysta—what patients are saying about their treatment journeys.',
      readTime: '11 min',
      date: 'Jan 10, 2025',
      category: 'Treatment'
    },
    {
      id: 6,
      title: 'Working with Lupus: Career Strategies from Real Patients',
      excerpt: 'How patients balance careers with chronic illness, including tips for workplace accommodations.',
      readTime: '6 min',
      date: 'Jan 8, 2025',
      category: 'Work & Life'
    },
    {
      id: 7,
      title: 'Lupus Nephritis: What Every Patient Should Know',
      excerpt: 'Kidney involvement is one of the most serious lupus complications. Here\'s what patients learned.',
      readTime: '8 min',
      date: 'Jan 5, 2025',
      category: 'Complications'
    },
    {
      id: 8,
      title: 'Finding the Right Rheumatologist: Patient Tips',
      excerpt: 'What to look for, questions to ask, and red flags according to experienced lupus patients.',
      readTime: '5 min',
      date: 'Jan 3, 2025',
      category: 'Healthcare'
    }
  ]
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Extract unique categories
  const categories = ['All', ...new Set(articles.map(a => a.category))];

  // Filter articles
  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredArticle = filteredArticles.find(a => a.featured);
  const regularArticles = filteredArticles.filter(a => !a.featured);

  return (
    <div
      style={{
        minHeight: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#272726',
        backgroundColor: '#f7f5f3'
      }}
    >
      {/* Header */}
      <header
        style={{
          backgroundColor: conditionColor,
          padding: '32px 20px 100px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background pattern */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: `
              radial-gradient(circle at 20% 30%, white 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, white 0%, transparent 40%)
            `,
            pointerEvents: 'none'
          }}
        />

        <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Back button */}
          <button
            onClick={onBack}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '30px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '32px',
              transition: 'background-color 0.2s ease'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            All Conditions
          </button>

          {/* Condition title */}
          <div style={{ marginBottom: '20px' }}>
            <h1
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(36px, 6vw, 56px)',
                fontWeight: '700',
                color: 'white',
                margin: 0,
                lineHeight: 1.1
              }}
            >
              {condition}
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '18px', marginTop: '12px', marginBottom: 0 }}>
              {articles.length} articles from real patients
            </p>
          </div>
        </div>

        {/* Wavy bottom */}
        <svg
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '50px',
            pointerEvents: 'none'
          }}
          viewBox="0 0 1440 50"
          preserveAspectRatio="none"
        >
          <path
            d="M0,25 C200,50 400,10 600,25 C800,40 1000,5 1200,20 C1350,30 1400,15 1440,25 L1440,50 L0,50 Z"
            fill="#f7f5f3"
          />
        </svg>
      </header>

      {/* Search and Filters */}
      <section
        style={{
          backgroundColor: 'white',
          padding: '24px 20px',
          borderBottom: '1px solid #e8e4e0',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <div
          style={{
            maxWidth: '1100px',
            margin: '0 auto',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '20px',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {/* Search */}
          <div style={{ position: 'relative', flex: '1', minWidth: '250px', maxWidth: '400px' }}>
            <input
              type="text"
              placeholder={`Search ${condition} articles...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 20px 14px 48px',
                fontSize: '15px',
                border: '2px solid #e8e4e0',
                borderRadius: '30px',
                backgroundColor: '#f7f5f3',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = conditionColor}
              onBlur={(e) => e.target.style.borderColor = '#e8e4e0'}
            />
            <svg
              style={{
                position: 'absolute',
                left: '18px',
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0.4
              }}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>

          {/* Category filters */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                style={{
                  padding: '10px 18px',
                  fontSize: '14px',
                  fontWeight: selectedCategory === category ? '600' : '400',
                  backgroundColor: selectedCategory === category ? conditionColor : 'transparent',
                  color: selectedCategory === category ? 'white' : '#666',
                  border: `1px solid ${selectedCategory === category ? conditionColor : '#ddd'}`,
                  borderRadius: '25px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <main style={{ padding: '40px 20px 80px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          {filteredArticles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <p style={{ fontSize: '18px', color: '#666' }}>
                No articles found matching "{searchQuery}"
              </p>
            </div>
          ) : (
            <>
              {/* Featured Article */}
              {featuredArticle && (
                <article
                  onClick={() => onArticleClick && onArticleClick(featuredArticle)}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    marginBottom: '40px',
                    cursor: 'pointer',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    alignItems: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)';
                  }}
                >
                  {/* Featured visual - Hand-drawn illustration style */}
                  <div
                    style={{
                      background: `linear-gradient(135deg, ${conditionColor}15 0%, ${conditionColor}30 100%)`,
                      padding: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '280px',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Artistic patient illustration */}
                    <svg viewBox="0 0 320 240" fill="none" style={{ width: '100%', maxWidth: '320px', height: 'auto' }}>
                      {/* Organic background shapes */}
                      <ellipse cx="160" cy="120" rx="140" ry="100" fill={`${conditionColor}15`} />
                      <ellipse cx="100" cy="140" rx="60" ry="50" fill={`${conditionColor}10`} />
                      <ellipse cx="240" cy="100" rx="50" ry="40" fill={`${conditionColor}10`} />

                      {/* Person 1 - Left */}
                      <g transform="translate(50, 60)">
                        {/* Head */}
                        <ellipse cx="40" cy="35" rx="28" ry="32" fill="#fce4d6" stroke="#272726" strokeWidth="2"/>
                        {/* Hair */}
                        <path d="M15 25 Q10 5 30 0 Q50 -3 62 10 Q70 25 65 35 L60 30 Q62 18 50 10 Q35 5 25 12 Q18 18 20 30 Z" fill="#4a3728" stroke="#272726" strokeWidth="1.5"/>
                        {/* Eyes */}
                        <ellipse cx="30" cy="35" rx="4" ry="3" fill="#272726"/>
                        <ellipse cx="50" cy="35" rx="4" ry="3" fill="#272726"/>
                        {/* Smile */}
                        <path d="M32 50 Q40 58 48 50" stroke="#272726" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                        {/* Body suggestion */}
                        <path d="M25 67 Q15 85 10 120 L70 120 Q65 85 55 67" fill={conditionColor} opacity="0.6" stroke="#272726" strokeWidth="1.5"/>
                      </g>

                      {/* Person 2 - Center */}
                      <g transform="translate(130, 40)">
                        {/* Head */}
                        <ellipse cx="40" cy="40" rx="32" ry="36" fill="#d4a574" stroke="#272726" strokeWidth="2"/>
                        {/* Hair - curly */}
                        <path d="M12 30 Q5 10 25 2 Q45 -5 60 5 Q78 15 72 35 Q75 25 70 20 Q60 5 40 8 Q20 10 18 25 Z" fill="#1a1a1a" stroke="#272726" strokeWidth="1.5"/>
                        <circle cx="15" cy="22" r="6" fill="#1a1a1a"/>
                        <circle cx="65" cy="20" r="5" fill="#1a1a1a"/>
                        {/* Eyes */}
                        <ellipse cx="28" cy="40" rx="5" ry="4" fill="#272726"/>
                        <ellipse cx="52" cy="40" rx="5" ry="4" fill="#272726"/>
                        {/* Warm smile */}
                        <path d="M28 58 Q40 68 52 58" stroke="#272726" strokeWidth="2" fill="none" strokeLinecap="round"/>
                        {/* Body */}
                        <path d="M20 76 Q5 100 0 140 L80 140 Q75 100 60 76" fill={conditionColor} opacity="0.7" stroke="#272726" strokeWidth="1.5"/>
                      </g>

                      {/* Person 3 - Right */}
                      <g transform="translate(210, 65)">
                        {/* Head */}
                        <ellipse cx="35" cy="32" rx="26" ry="30" fill="#f5deb3" stroke="#272726" strokeWidth="2"/>
                        {/* Hair - short gray */}
                        <path d="M12 22 Q10 5 28 0 Q45 -2 55 8 Q62 18 58 28 L52 24 Q55 15 45 8 Q30 5 20 12 Q15 18 17 24 Z" fill="#9ca3af" stroke="#272726" strokeWidth="1.5"/>
                        {/* Eyes */}
                        <ellipse cx="25" cy="32" rx="4" ry="3" fill="#272726"/>
                        <ellipse cx="45" cy="32" rx="4" ry="3" fill="#272726"/>
                        {/* Glasses */}
                        <rect x="18" y="27" width="16" height="12" rx="2" fill="none" stroke="#272726" strokeWidth="1.5"/>
                        <rect x="38" y="27" width="16" height="12" rx="2" fill="none" stroke="#272726" strokeWidth="1.5"/>
                        <line x1="34" y1="32" x2="38" y2="32" stroke="#272726" strokeWidth="1.5"/>
                        {/* Smile */}
                        <path d="M27 48 Q35 54 43 48" stroke="#272726" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                        {/* Body */}
                        <path d="M20 62 Q10 80 5 115 L65 115 Q60 80 50 62" fill={conditionColor} opacity="0.5" stroke="#272726" strokeWidth="1.5"/>
                      </g>

                      {/* Decorative elements */}
                      <circle cx="45" cy="200" r="8" fill={conditionColor} opacity="0.3"/>
                      <circle cx="280" cy="180" r="6" fill={conditionColor} opacity="0.4"/>
                      <circle cx="160" cy="220" r="10" fill={conditionColor} opacity="0.2"/>
                    </svg>

                    {/* Featured badge */}
                    <span
                      style={{
                        position: 'absolute',
                        top: '20px',
                        right: '20px',
                        backgroundColor: conditionColor,
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}
                    >
                      Featured
                    </span>
                  </div>

                  {/* Content */}
                  <div style={{ padding: '40px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        backgroundColor: `${conditionColor}15`,
                        color: conditionColor,
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        marginBottom: '16px'
                      }}
                    >
                      {featuredArticle.category}
                    </span>

                    <h2
                      style={{
                        fontFamily: '"Source Serif Pro", Georgia, serif',
                        fontSize: 'clamp(24px, 3vw, 32px)',
                        fontWeight: '700',
                        lineHeight: 1.25,
                        marginTop: 0,
                        marginBottom: '16px',
                        color: '#272726'
                      }}
                    >
                      {featuredArticle.title}
                    </h2>

                    <p
                      style={{
                        fontSize: '17px',
                        color: '#555',
                        lineHeight: 1.7,
                        marginBottom: '20px'
                      }}
                    >
                      {featuredArticle.excerpt}
                    </p>

                    <div
                      style={{
                        display: 'flex',
                        gap: '16px',
                        fontSize: '14px',
                        color: '#888'
                      }}
                    >
                      <span>{featuredArticle.date}</span>
                      <span>•</span>
                      <span>{featuredArticle.readTime} read</span>
                    </div>
                  </div>
                </article>
              )}

              {/* Regular Articles Grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '24px'
                }}
              >
                {regularArticles.map((article) => (
                  <article
                    key={article.id}
                    onClick={() => onArticleClick && onArticleClick(article)}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
                    }}
                  >
                    {/* Color accent */}
                    <div
                      style={{
                        height: '6px',
                        background: conditionColor
                      }}
                    />

                    <div style={{ padding: '24px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          backgroundColor: `${conditionColor}10`,
                          color: conditionColor,
                          fontSize: '11px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          padding: '5px 12px',
                          borderRadius: '15px',
                          marginBottom: '14px'
                        }}
                      >
                        {article.category}
                      </span>

                      <h3
                        style={{
                          fontFamily: '"Source Serif Pro", Georgia, serif',
                          fontSize: '18px',
                          fontWeight: '600',
                          lineHeight: 1.35,
                          marginTop: 0,
                          marginBottom: '12px',
                          color: '#272726'
                        }}
                      >
                        {article.title}
                      </h3>

                      <p
                        style={{
                          fontSize: '14px',
                          color: '#666',
                          lineHeight: 1.6,
                          marginBottom: '16px',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {article.excerpt}
                      </p>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '12px',
                          color: '#999'
                        }}
                      >
                        <span>{article.date}</span>
                        <span>{article.readTime} read</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>

          {/* Community CTA */}
          {onCommunityClick && (
            <div
              style={{
                marginTop: '48px',
                background: '#fff',
                borderRadius: '16px',
                padding: '40px',
                textAlign: 'center',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
              }}
            >
              <h3
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: 'clamp(20px, 3vw, 26px)',
                  fontWeight: '700',
                  color: '#272726',
                  margin: '0 0 12px'
                }}
              >
                Hear from real {condition} patients.
              </h3>
              <p style={{ fontSize: '15px', color: '#666', margin: '0 0 24px', lineHeight: 1.6 }}>
                See what others are saying about living with {condition} — and add your voice.
              </p>
              <button
                onClick={onCommunityClick}
                style={{
                  backgroundColor: '#272726',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '14px 32px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a39'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#272726'}
              >
                Join the Conversation
              </button>
            </div>
          )}
      </main>
    </div>
  );
};
